/**
 * Symbol Resolver Module
 *
 * Resolves contract descriptions from AMP PDFs to standard futures symbols.
 * Uses verified AMP Client Summary field mappings.
 *
 * Matching strategy:
 * 1. Exact match against verified mappings
 * 2. Match with trailing digits stripped (e.g., "MGC1 FUTURE" -> "MGC FUTURE")
 * 3. Returns UNKNOWN for non-matches (logged for admin review)
 */

import symbolMappings from './symbol-mappings.json';

// Type for the mappings
const mappings = symbolMappings.mappings as Record<string, string>;

// Track unmapped descriptions for this execution (for logging/notification)
const unmappedDescriptions: Set<string> = new Set();

// Month abbreviations
const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

export interface ResolvedContract {
  symbol: string;
  instrumentType: 'FUTURE' | 'OPTION';
  optionType: 'CALL' | 'PUT' | null;
  contractMonth: string;
  matchMethod: 'exact' | 'normalized' | 'unknown';
  originalDescription: string;
}

/**
 * Resolve contract description parts to a standard symbol
 */
export function resolveSymbol(descriptionParts: string[]): ResolvedContract {
  // Join parts and normalize
  const rawDescription = descriptionParts.join(' ').trim();
  const upperDescription = rawDescription.toUpperCase();

  // Default result
  const result: ResolvedContract = {
    symbol: 'UNKNOWN',
    instrumentType: 'FUTURE',
    optionType: null,
    contractMonth: '',
    matchMethod: 'unknown',
    originalDescription: rawDescription
  };

  // Extract month/year if present (e.g., "DEC 25", "JAN 26")
  for (let i = 0; i < descriptionParts.length; i++) {
    const part = descriptionParts[i].toUpperCase();
    if (MONTHS.includes(part)) {
      // Check if next part is a 2-digit year
      if (i + 1 < descriptionParts.length && /^\d{2}$/.test(descriptionParts[i + 1])) {
        result.contractMonth = `${part} ${descriptionParts[i + 1]}`;
      } else {
        result.contractMonth = part;
      }
      break;
    }
  }

  // Detect option vs future
  if (/\bOPTION\b/i.test(upperDescription)) {
    result.instrumentType = 'OPTION';
    // Check for call/put indicator
    if (/\bC\b|\bCALL\b/i.test(upperDescription)) {
      result.optionType = 'CALL';
    } else if (/\bP\b|\bPUT\b/i.test(upperDescription)) {
      result.optionType = 'PUT';
    }
  }

  // Build the lookup key: strip month/year and normalize
  // e.g., "MES Future DEC 25" -> "MES FUTURE"
  let lookupKey = upperDescription
    .replace(/\b(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)\b/gi, '')
    .replace(/\b\d{2}\b/g, '')  // Remove 2-digit years
    .replace(/\s+/g, ' ')
    .trim();

  // Method 1: Exact match
  if (mappings[lookupKey]) {
    result.symbol = mappings[lookupKey];
    result.matchMethod = 'exact';
    return result;
  }

  // Method 2: Try without trailing digits on first word (MGC1 -> MGC)
  const normalizedKey = lookupKey.replace(/^([A-Z]+)\d+/, '$1');
  if (normalizedKey !== lookupKey && mappings[normalizedKey]) {
    result.symbol = mappings[normalizedKey];
    result.matchMethod = 'normalized';
    return result;
  }

  // Method 3: Try partial matches - check if any mapping key is contained
  for (const [key, symbol] of Object.entries(mappings)) {
    if (lookupKey.includes(key) || key.includes(lookupKey)) {
      result.symbol = symbol;
      result.matchMethod = 'normalized';
      return result;
    }
  }

  // No match found - log for admin review
  if (!unmappedDescriptions.has(lookupKey)) {
    unmappedDescriptions.add(lookupKey);
    console.warn(`[UNMAPPED] Contract description not found: "${lookupKey}" (original: "${rawDescription}")`);
  }

  return result;
}

/**
 * Get all unmapped descriptions from this execution
 */
export function getUnmappedDescriptions(): string[] {
  return Array.from(unmappedDescriptions);
}

/**
 * Clear unmapped descriptions (call between PDF processing if needed)
 */
export function clearUnmappedDescriptions(): void {
  unmappedDescriptions.clear();
}

/**
 * Parse contract info - wrapper that returns all fields including originalDescription
 */
export function parseContractInfo(parts: string[]): {
  contractType: string;
  contractMonth: string;
  instrumentType: 'FUTURE' | 'OPTION';
  optionType: 'CALL' | 'PUT' | null;
  originalDescription: string;
} {
  const resolved = resolveSymbol(parts);

  return {
    contractType: resolved.symbol,
    contractMonth: resolved.contractMonth,
    instrumentType: resolved.instrumentType,
    optionType: resolved.optionType,
    originalDescription: resolved.originalDescription
  };
}

